// Estilo "navy" compartilhado: o mesmo visual do banner do Dashboard e do
// modal de Contato (azul-noite com brilho verde). Só aparência.
// Use dentro do makeStyles de cada página/modal: { hero: heroNavy(theme), ... }

export const VERDE = "#1E9855";
export const VERDE_CLARO = "#34d399";

const escuroDe = (theme) => theme.palette.type === "dark";

export const fundoNavy =
  "radial-gradient(420px 200px at 85% 0%, rgba(34,197,94,0.30), transparent 70%)," +
  "radial-gradient(360px 180px at 0% 100%, rgba(34,197,94,0.14), transparent 70%)," +
  // grafite nos dois temas (antes: azul-marinho)
  "linear-gradient(120deg, #111816 0%, #151d1a 55%, #10261d 100%)";

// Modo escuro (grafite): mesmo desenho, sem o azul-marinho.
export const fundoGrafite =
  "radial-gradient(420px 200px at 85% 0%, rgba(34,197,94,0.22), transparent 70%)," +
  "radial-gradient(360px 180px at 0% 100%, rgba(34,197,94,0.10), transparent 70%)," +
  "linear-gradient(120deg, #111816 0%, #151d1a 55%, #10261d 100%)";

/** Fundo do hero/banner conforme o tema: escuro = grafite, claro = navy. */
export const fundoDe = (theme) => (escuroDe(theme) ? fundoGrafite : fundoNavy);

// Grade sutil + brilho no canto (vai nos ::before/::after do hero/banner).
const decoracao = {
  "&::before": {
    content: '""',
    position: "absolute",
    width: 320,
    height: 320,
    right: -90,
    top: -160,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0) 70%)",
    pointerEvents: "none",
  },
  "&::after": {
    content: '""',
    position: "absolute",
    inset: 0,
    backgroundImage:
      "linear-gradient(rgba(255,255,255,0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.06) 1px, transparent 1px)",
    backgroundSize: "22px 22px",
    maskImage: "linear-gradient(90deg, transparent 0%, #000 65%)",
    WebkitMaskImage: "linear-gradient(90deg, transparent 0%, #000 65%)",
    pointerEvents: "none",
  },
  "& > *": { position: "relative", zIndex: 1 },
};

// ───────────────────────── Banner de página ─────────────────────────
export const bannerNavy = (theme) => ({
  position: "relative",
  overflow: "hidden",
  padding: "22px 26px",
  borderRadius: 20,
  color: "#fff",
  background: fundoDe(theme),
  border: escuroDe(theme) ? "1px solid rgba(148,163,184,.14)" : "none",
  boxShadow: escuroDe(theme) ? "0 12px 32px rgba(0,0,0,.4)" : "0 14px 34px rgba(17,24,20,.18)",
  animation: "iaSubir .4s ease backwards",
  ...decoracao,
  "& .MuiSvgIcon-root": { color: "#fff" },
  "& .MuiTypography-root, & h1, & h2, & h3, & h4, & h5, & h6": { color: "#fff" },
  "& .MuiTypography-body2, & .MuiTypography-colorTextSecondary, & p": { color: "#94a3b8 !important" },
});

export const bannerTitulo = {
  fontSize: "1.35rem",
  fontWeight: 800,
  color: "#fff",
  display: "flex",
  alignItems: "center",
  gap: 10,
  lineHeight: 1.2,
};

export const bannerSub = { fontSize: "0.86rem", color: "#94a3b8", marginTop: 4 };

// Botões que ficam sobre o fundo navy.
export const botaoVidro = {
  borderRadius: 12,
  textTransform: "none",
  fontWeight: 700,
  padding: "8px 16px",
  color: "#fff !important",
  backgroundColor: "rgba(255,255,255,0.08)",
  border: "1px solid rgba(255,255,255,0.18)",
  backdropFilter: "blur(6px)",
  "&:hover": { backgroundColor: "rgba(255,255,255,0.16)" },
  "&.Mui-disabled": { opacity: 0.5 },
};

export const botaoVerdeBrilho = {
  borderRadius: 12,
  textTransform: "none",
  fontWeight: 800,
  padding: "8px 18px",
  color: "#fff !important",
  background: `linear-gradient(135deg, #22c55e, ${VERDE})`,
  boxShadow: "0 8px 20px rgba(34,197,94,0.35)",
  "&:hover": { background: "linear-gradient(135deg, #16a34a, #15803d)" },
};

// Mini-indicadores do banner (ex.: "6 conectadas").
export const bannerStats = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
  marginTop: 16,
};
export const bannerStat = {
  minWidth: 120,
  padding: "10px 14px",
  borderRadius: 14,
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.12)",
  backdropFilter: "blur(6px)",
  animation: "iaSubir .45s ease backwards",
};
export const bannerStatValor = { fontSize: "1.3rem", fontWeight: 800, color: "#fff", lineHeight: 1.1 };
export const bannerStatRotulo = {
  fontSize: "0.68rem",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "#94a3b8",
  marginTop: 3,
  display: "flex",
  alignItems: "center",
  gap: 6,
};

// ───────────────────────── Modal ─────────────────────────
export const modalPapel = (theme, largura = 640) => ({
  borderRadius: 24,
  width: "100%",
  maxWidth: largura,
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
  backgroundColor: escuroDe(theme) ? "#111614" : "#f4f7fb",
  boxShadow: escuroDe(theme)
    ? "0 30px 80px rgba(0,0,0,0.55), 0 0 0 1px rgba(52,211,153,0.12)"
    : "0 30px 80px rgba(15,23,42,0.22)",
  animation: "iaModalEntrar .22s ease-out",
});

export const modalHero = (theme) => ({
  position: "relative",
  padding: theme.spacing(2.75, 3, 2.25, 3),
  background: fundoNavy,
  color: "#fff",
  flexShrink: 0,
  overflow: "hidden",
  ...decoracao,
});

export const modalHeroLinha = {
  display: "flex",
  alignItems: "center",
  gap: 14,
};

export const modalHeroIcone = {
  width: 48,
  height: 48,
  borderRadius: 14,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  background: "linear-gradient(135deg, #22c55e, #15803d)",
  boxShadow: "0 0 0 3px rgba(255,255,255,0.14), 0 8px 22px rgba(0,0,0,0.3)",
  "& svg": { fontSize: 26, color: "#fff" },
};

export const modalHeroEtiqueta = {
  fontSize: "0.66rem",
  fontWeight: 700,
  letterSpacing: "0.14em",
  textTransform: "uppercase",
  color: "#86efac",
};

export const modalHeroTitulo = {
  fontSize: "1.3rem",
  fontWeight: 800,
  lineHeight: 1.2,
  color: "#fff",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

export const modalHeroSub = { fontSize: "0.82rem", color: "#94a3b8", marginTop: 2 };

export const modalHeroChip = {
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  padding: "4px 10px",
  borderRadius: 999,
  fontSize: "0.76rem",
  fontWeight: 600,
  color: "#fff",
  backgroundColor: "rgba(255,255,255,0.12)",
  border: "1px solid rgba(255,255,255,0.22)",
  backdropFilter: "blur(6px)",
  "& svg": { fontSize: "0.95rem", color: "#fff" },
};

export const modalFechar = {
  color: "#fff !important",
  backgroundColor: "rgba(255,255,255,0.12)",
  "&:hover": { backgroundColor: "rgba(255,255,255,0.24)" },
  "& svg": { color: "#fff" },
};

export const modalCorpo = (theme) => ({
  padding: theme.spacing(2.5, 3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  overflowY: "auto",
  overscrollBehavior: "contain",
  "&::-webkit-scrollbar": { width: 6 },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: escuroDe(theme) ? "rgba(255,255,255,0.18)" : "rgba(30,152,85,0.3)",
    borderRadius: 10,
  },
});

export const modalSecao = (theme) => ({
  borderRadius: 18,
  padding: theme.spacing(2, 2.25),
  backgroundColor: escuroDe(theme) ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.9)",
  border: `1px solid ${escuroDe(theme) ? "rgba(255,255,255,0.08)" : "rgba(15,23,42,0.07)"}`,
  boxShadow: escuroDe(theme) ? "none" : "0 6px 20px rgba(15,23,42,0.05)",
  animation: "iaSubir .35s ease backwards",
});

export const modalSecaoTopo = { display: "flex", alignItems: "center", gap: 10, marginBottom: 14 };

export const modalSecaoIcone = {
  width: 30,
  height: 30,
  borderRadius: 10,
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  background: `linear-gradient(135deg, ${VERDE_CLARO}, ${VERDE})`,
  boxShadow: "0 4px 12px rgba(30,152,85,0.35)",
  "& svg": { fontSize: "1.05rem", color: "#fff" },
};

export const modalSecaoTitulo = (theme) => ({
  fontSize: "0.95rem",
  fontWeight: 800,
  color: theme.palette.text.primary,
});

export const modalSecaoSub = (theme) => ({
  fontSize: "0.76rem",
  color: theme.palette.text.secondary,
});

export const modalRodape = (theme) => ({
  padding: theme.spacing(1.75, 3),
  display: "flex",
  alignItems: "center",
  gap: 10,
  flexShrink: 0,
  borderTop: `1px solid ${escuroDe(theme) ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.07)"}`,
  backgroundColor: escuroDe(theme) ? "#111614" : "#ffffff",
});

export const campoNavy = (theme) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    backgroundColor: escuroDe(theme) ? "rgba(255,255,255,0.03)" : "#fff",
    fontSize: "0.9rem",
    transition: "box-shadow .2s ease",
    "& fieldset": { borderColor: escuroDe(theme) ? "rgba(255,255,255,0.12)" : "#e2e8f0" },
    "&:hover fieldset": { borderColor: VERDE },
    "&.Mui-focused": { boxShadow: "0 0 0 4px rgba(30,152,85,0.12)" },
    "&.Mui-focused fieldset": { borderColor: VERDE, borderWidth: "1.5px" },
  },
  "& .MuiInputLabel-outlined.Mui-focused": { color: VERDE },
});

export const botaoPrimario = {
  borderRadius: 12,
  padding: "8px 22px",
  textTransform: "none",
  fontWeight: 800,
  color: "#fff",
  background: `linear-gradient(135deg, #22c55e, ${VERDE})`,
  boxShadow: "0 6px 16px rgba(30,152,85,0.3)",
  "&:hover": { background: "linear-gradient(135deg, #16a34a, #15803d)" },
  "&.Mui-disabled": { color: "rgba(255,255,255,0.8)", opacity: 0.55 },
};

export const botaoSecundario = (theme) => ({
  borderRadius: 12,
  padding: "8px 18px",
  textTransform: "none",
  fontWeight: 700,
  color: theme.palette.text.secondary,
  border: `1px solid ${escuroDe(theme) ? "rgba(255,255,255,0.16)" : "#d0d5dd"}`,
  "&:hover": { backgroundColor: escuroDe(theme) ? "rgba(255,255,255,0.06)" : "#f3f4f6" },
});

export const botaoPerigo = {
  borderRadius: 12,
  padding: "8px 14px",
  textTransform: "none",
  fontWeight: 700,
  color: "#e53935",
  "&:hover": { backgroundColor: "rgba(229,57,53,0.08)" },
};

export const botaoPerigoCheio = {
  borderRadius: 12,
  padding: "8px 16px",
  textTransform: "none",
  fontWeight: 800,
  color: "#fff",
  backgroundColor: "#e53935",
  boxShadow: "0 6px 16px rgba(229,57,53,0.3)",
  "&:hover": { backgroundColor: "#c62828" },
};
